import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, Phone, MapPin, Cylinder, Gauge, Clock,
  AlertTriangle, CheckCircle2, RefreshCw, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Staff-facing household view opened after a successful QR scan.
 * Distinct from the public /h/:id card — this exposes contact + meter
 * identifiers and allows recording a cylinder replacement.
 */
export default function FieldHouseholdView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapNote, setSwapNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/field/households/${id}`);
      setSnap(data);
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || "Failed to load household");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSwap = async () => {
    if (!snap?.meter?.id) return;
    setSaving(true);
    try {
      await api.post(`/meters/${snap.meter.id}/cylinder/swap`, { note: swapNote });
      toast.success("Cylinder marked as replaced");
      setSwapOpen(false); setSwapNote("");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to record swap");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="text-slate-500 text-sm py-10 text-center" data-testid="field-hh-loading">Loading…</div>;
  }
  if (err) {
    return (
      <div className="space-y-3" data-testid="field-hh-error">
        <div className="border border-red-800 bg-red-950/40 text-red-300 text-sm px-3 py-2 rounded-sm">{err}</div>
        <Button onClick={() => nav("/field")} variant="outline" className="rounded-sm">Back to home</Button>
      </div>
    );
  }
  if (!snap) return null;

  const { household, meter, cylinder, currency } = snap;
  const price = snap.gas_price_per_m3 || 0;
  const pct = cylinder?.remaining_pct ?? null;
  const barColor = pct == null ? "bg-slate-700" : pct < (cylinder?.threshold_pct ?? 15) ? "bg-red-500" : pct < 40 ? "bg-flame-500" : "bg-emerald-500";

  return (
    <div className="space-y-5" data-testid="field-hh-page">
      <div className="flex items-center gap-2">
        <button
          onClick={() => nav("/field")}
          data-testid="field-hh-back-btn"
          className="text-slate-400 hover:text-white -ml-1 p-1"
          aria-label="Back"
        >
          <ChevronLeft size={22} />
        </button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-flame-400 font-semibold">Scan Result</div>
          <h1 className="font-heading text-xl font-bold text-white leading-tight" data-testid="field-hh-name">
            {household?.household_name || "—"}
          </h1>
        </div>
      </div>

      {/* Contact + area */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2">
        {household?.phone && (
          <a
            href={`tel:+${household.phone}`}
            data-testid="field-hh-phone"
            className="flex items-center gap-3 text-slate-200 hover:text-flame-400"
          >
            <Phone size={16} className="text-flame-400 flex-shrink-0" />
            <span className="tabular-nums">+{household.phone}</span>
          </a>
        )}
        {(household?.address || household?.area_name) && (
          <div className="flex items-start gap-3 text-slate-300 text-sm">
            <MapPin size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <span>
              {household?.address}
              {household?.area_name && <span className="text-slate-500"> · {household.area_name}</span>}
            </span>
          </div>
        )}
        {household?.household_number && (
          <div className="text-[11px] text-slate-500 uppercase tracking-widest">HH# {household.household_number}</div>
        )}
      </div>

      {/* Meter status */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3" data-testid="field-hh-meter">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Meter</div>
          {meter ? (
            <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-widest font-semibold ${
              meter.valve_state === "OPEN"
                ? "bg-emerald-950 text-emerald-300 border border-emerald-800"
                : "bg-red-950 text-red-300 border border-red-800"
            }`}>
              Valve {meter.valve_state || "?"}
            </span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-widest font-semibold bg-slate-800 text-slate-400 border border-slate-700">No meter</span>
          )}
        </div>

        {meter ? (
          <>
            <div className="font-mono text-sm text-white tabular-nums">{meter.serial_number || "—"}</div>
            {meter.imei && <div className="text-[11px] text-slate-500 tabular-nums">IMEI {meter.imei}</div>}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Stat
                icon={<Gauge size={14} />}
                label="Reading"
                value={`${(meter.reading_m3 ?? 0).toFixed(3)} m³`}
              />
              <Stat
                icon={<Clock size={14} />}
                label="Last report"
                value={meter.last_report_at ? new Date(meter.last_report_at).toLocaleString() : "—"}
              />
            </div>
            {typeof meter.balance_ugx === "number" && (
              <div className="pt-2 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Credit balance</div>
                <div className="font-heading text-lg font-bold text-white tabular-nums mt-0.5">
                  {currency} {meter.balance_ugx.toLocaleString()}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-slate-500 text-sm">This household has no meter assigned yet.</div>
        )}
      </div>

      {/* Cylinder */}
      {cylinder && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3" data-testid="field-hh-cylinder">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cylinder size={16} className="text-flame-400" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Cylinder ({cylinder.cylinder_kg} kg)</div>
            </div>
            {cylinder.is_low && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-widest font-semibold bg-red-950 text-red-300 border border-red-800">
                <AlertTriangle size={11} /> Low
              </span>
            )}
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <div className="font-heading text-3xl font-bold text-white tabular-nums">{pct ?? "—"}%</div>
              <div className="text-xs text-slate-400 tabular-nums">
                {cylinder.remaining_kg?.toFixed(2)} kg left
              </div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-800 overflow-hidden">
              <div
                className={`h-full ${barColor} transition-all`}
                style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Stat
              icon={<Clock size={14} />}
              label="Days to empty"
              value={cylinder.days_to_empty != null ? `${cylinder.days_to_empty}` : "—"}
            />
            <Stat
              icon={<Gauge size={14} />}
              label="Avg / day"
              value={`${(cylinder.avg_daily_m3 ?? 0).toFixed(3)} m³`}
            />
          </div>

          {cylinder.last_swap_at && (
            <div className="text-[11px] text-slate-500 pt-1">
              Last swap · {new Date(cylinder.last_swap_at).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Primary action */}
      {meter && (
        <button
          onClick={() => setSwapOpen(true)}
          data-testid="field-hh-swap-btn"
          className="w-full rounded-2xl bg-gradient-to-r from-flame-500 to-flame-700 text-white shadow-lg shadow-flame-900/30 py-5 flex items-center justify-center gap-3 active:scale-[0.99] transition-transform hover:brightness-110"
        >
          <RefreshCw size={22} />
          <span className="font-heading text-lg font-bold">Mark cylinder replaced</span>
        </button>
      )}

      <button
        onClick={() => nav("/field/scan")}
        data-testid="field-hh-scan-again-btn"
        className="w-full text-slate-400 hover:text-white text-xs uppercase tracking-widest font-semibold py-3 border border-slate-800 rounded-lg flex items-center justify-center gap-2"
      >
        Scan another <ArrowRight size={13} />
      </button>

      {/* Swap confirmation dialog */}
      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="max-w-md rounded-xl bg-slate-900 border-slate-800 text-slate-100" data-testid="field-swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-white">
              <CheckCircle2 size={18} className="text-emerald-400" /> Confirm cylinder replacement
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This resets the fill baseline for meter{" "}
              <span className="text-slate-200 font-mono">{meter?.serial_number}</span>. The customer&apos;s
              credit balance is not affected.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Note (optional)</label>
            <Textarea
              value={swapNote}
              onChange={(e) => setSwapNote(e.target.value)}
              placeholder="Cylinder batch, ID, condition…"
              rows={3}
              data-testid="field-swap-note-input"
              className="mt-1 rounded-sm bg-slate-950 border-slate-800 text-white"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button" variant="outline"
              onClick={() => setSwapOpen(false)}
              className="rounded-sm border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800"
              data-testid="field-swap-cancel-btn"
            >Cancel</Button>
            <Button
              onClick={submitSwap}
              disabled={saving}
              data-testid="field-swap-confirm-btn"
              className="bg-flame-600 hover:bg-flame-700 rounded-sm"
            >
              {saving ? "Recording…" : "Confirm swap"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1 font-semibold">
      {icon} {label}
    </div>
    <div className="text-sm text-white font-semibold tabular-nums mt-0.5 truncate">{value}</div>
  </div>
);
